import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import api from '../../api/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faChevronRight, faFileAlt, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import '../../styles/admin/RegistrationListPage.css';

const RegistrationItem = ({ item, onPress }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <div className="registration-item" onClick={onPress}>
      <div className="registration-header">
        <h3 className="registration-name">{item.parentName}</h3>
        <div className={`status-badge ${item.status}-badge`}>
          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
        </div>
      </div>
      
      <div className="registration-info">
        <p className="registration-email">{item.parentEmail}</p>
        <p className="registration-phone">{item.parentPhone}</p>
      </div>
      
      {item.hasChild && (
        <div className="child-info">
          <FontAwesomeIcon icon={faUser} className="child-icon" />
          <span className="child-text">Child: {item.childInfo.name}</span>
        </div>
      )}
      
      <div className="registration-footer">
        <span className="registration-date">
          Submitted: {formatDate(item.createdAt || item.submittedAt)}
        </span>
        <FontAwesomeIcon icon={faChevronRight} className="chevron-icon" />
      </div>
    </div>
  );
};

const RegistrationListPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('pending');
  
  // Fetch registrations
  const fetchRegistrations = async (status = activeFilter) => {
    try {
      setLoading(true);
      const centerId = user?.center || '';
      
      const response = await api.get('/registrations', {
        params: { 
          status, 
          centerId: user?.center || user?.id || undefined 
        }
      });
      
      if (response.data.success) {
        setRegistrations(response.data.data);
      } else {
        console.warn('No successful response:', response.data);
        setRegistrations([]);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error.response?.data || error.message);
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle status filter change
  const handleFilterChange = (status) => {
    setActiveFilter(status);
    fetchRegistrations(status);
  };
  
  // Navigate to registration details
  const handleViewRegistration = (item) => {
    navigate(`/admin/registrations/${item._id}`);
  };
  
  // Initialize
  useEffect(() => {
    fetchRegistrations();
  }, []);
  
  return (
    <div className="registrations-container">
      <div className="page-header">
        <div className="header-with-back">
          <Link to="/dashboard" className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
          </Link>
          <h1 className="page-title">Registration Requests</h1>
        </div>
      </div>
      
      <div className="filter-container">
        <button 
          className={`filter-button ${activeFilter === 'pending' ? 'active-filter' : ''}`}
          onClick={() => handleFilterChange('pending')}
        >
          Pending
        </button>
        <button 
          className={`filter-button ${activeFilter === 'approved' ? 'active-filter' : ''}`}
          onClick={() => handleFilterChange('approved')}
        >
          Approved
        </button>
        <button 
          className={`filter-button ${activeFilter === 'rejected' ? 'active-filter' : ''}`}
          onClick={() => handleFilterChange('rejected')}
        >
          Rejected
        </button>
      </div>
      
      <div className="registrations-list">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading registrations...</p>
          </div>
        ) : registrations.length > 0 ? (
          registrations.map(item => (
            <RegistrationItem 
              key={item._id} 
              item={item} 
              onPress={() => handleViewRegistration(item)} 
            />
          ))
        ) : (
          <div className="empty-container">
            <FontAwesomeIcon icon={faFileAlt} className="empty-icon" />
            <p className="empty-text">No {activeFilter} registrations found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegistrationListPage;